import type { TodayChecks, TodayKind } from '../cms/todayData';
import { toDate } from '../reach/reach';

/**
 * The Approvals review's words (design 2026-10-27, §5 and §7): how long an
 * item has waited, the CHECKS line, what approving does, the fix button, and
 * the toast for Ask for changes. Kept apart from the queue so they're tested
 * on their own.
 */

export const DAY = 86400000;

export const time = (d: Date) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
const weekday = (d: Date) => d.toLocaleDateString([], { weekday: 'short' });
export const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** "22m", "3h", "2d" — how long something has waited. */
export function ago(iso?: string | null) {
  const d = toDate(iso);
  if (!d) return '';
  const min = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
  if (min < 60) return `${min}m`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h}h`;
  // Units that keep the number small: "280d" read as a code, "9mo" doesn't.
  const days = Math.round(h / 24);
  if (days < 7) return `${days}d`;
  if (days < 60) return `${Math.round(days / 7)}w`;
  if (days < 365) return `${Math.round(days / 30)}mo`;
  return `${Math.round(days / 365)}y`;
}

/** "Today 6:00 PM", "Tomorrow 9:00 AM", "Sun 9:00 AM", "Oct 30, 9:00 AM". */
export function when(d: Date, now = new Date()) {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const days = Math.floor((d.getTime() - start.getTime()) / DAY);
  if (days === 0) return `Today ${time(d)}`;
  if (days === 1) return `Tomorrow ${time(d)}`;
  if (days > 1 && days < 7) return `${weekday(d)} ${time(d)}`;
  return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${time(d)}`;
}

export const reviewHref = (kind: TodayKind | 'dataset', id: string) =>
  kind === 'article' || kind === 'dataset'
    ? `/content/${id}`
    : kind === 'page'
      ? `/content?view=pages&page=${id}`
      : kind === 'form'
        ? `/content?view=forms&form=${id}`
        : '/content?view=manage&area=design';

/** The fix button's words for an item's first open check ("Fix claim", "Add alt text"). */
export function fixLabel(check: string): string {
  if (/copy edit hasn.t run/i.test(check)) return 'Run copy edit';
  if (/alt text/i.test(check)) return 'Add alt text';
  if (/no image|no share image|image missing/i.test(check)) return 'Add image';
  if (/claim/i.test(check)) return 'Fix claim';
  if (/link/i.test(check)) return 'Fix link';
  return 'Fix';
}

/** The check line's own words, for what the agent is asked to fix. */
export const heldWords = (line: string | null | undefined) =>
  String(line || '')
    .replace(/^●\s*/, '')
    .replace(/\s*\(\+\d+ more\)$/, '');

export type CheckTone = 'ok' | 'warn' | 'block' | 'wait';
export interface CheckLine {
  text: string;
  tone: CheckTone;
}

export function checkLine(c: TodayChecks | null | undefined): CheckLine | null {
  if (!c) return null;
  const more = c.more > 0 ? ` (+${c.more} more)` : '';
  if (c.blocking) return { tone: 'block', text: `● ${c.first}${more}` };
  if (c.warnings) return { tone: 'warn', text: `● ${c.first}${more}` };
  // Wording suggestions aren't problems: a plain approve.
  const n = c.suggestions || 0;
  return {
    tone: 'ok',
    text: n
      ? `✓ Checks pass · ${n} wording ${n === 1 ? 'suggestion' : 'suggestions'}`
      : '✓ Checks pass',
  };
}

export const NOTHING_TO_CHECK: CheckLine = { tone: 'ok', text: '✓ Nothing to fix' };

/** Not known yet: never "Nothing to fix" for an article nobody has checked (backlog aec235dc). */
export const CHECKS_LOADING: CheckLine = { tone: 'wait', text: 'Running checks…' };
export const CHECKS_FAILED: CheckLine = {
  tone: 'warn',
  text: '● Checks didn’t load. Open it to see them.',
};

/**
 * A page whose copy edit runs as part of approving: a passing line says so,
 * beside the Skip copy edit that approves without it. An open check already
 * leads with its own line.
 */
export function copyEditLine(line: CheckLine, pending: boolean): CheckLine {
  return pending && line.tone === 'ok'
    ? { tone: 'ok', text: `${line.text} · copy edit runs when you approve` }
    : line;
}

export const CHIP: Record<TodayKind, [string, string]> = {
  article: ['Publish', 'Update'],
  page: ['New page', 'Page change'],
  form: ['New form', 'Form change'],
  site: ['New site', 'Site change'],
};

/** What approving does, in the CHECKS box (design §7). */
export const EFFECT = {
  publish: 'Approving publishes it to your site right away.',
  schedule: 'Approving schedules it. Until then nothing is public.',
  site: 'Approving publishes these changes to your live site.',
  together: 'Approving publishes them together, right away.',
  form: 'Approving publishes the form on the pages that place it.',
  dataset: 'Approving publishes the dataset with its article. Readers see it right away.',
  campaign: 'Approving puts its posts on the calendar. Nothing goes out before its dates.',
  alwaysOn: 'Turning it on lets it post on its own, as its rules say.',
  ended:
    'Rescheduling asks your agent for new dates. Dismissing takes it off this list; nothing is deleted.',
  draft: 'Scheduling starts it on its dates. Until then nothing goes out.',
  agent:
    'Approving lets your agent make this one change in the next 15 minutes. It still can’t publish anything without your approval.',
  support:
    'Approving lets tinycms support in for the time you pick. Everything they do is in the Activity log.',
} as const;

/**
 * What approving waiting posts does. "As planned" only while every post's time
 * is still ahead: a passed one goes out now (UX review round 26).
 */
export function postsEffect(total: number, passed: number) {
  const tail = ' Nothing is public until then.';
  if (!passed) return `Approving lets ${total === 1 ? 'it' : 'them'} go out as planned.${tail}`;
  if (passed === total) return `Approving posts ${total === 1 ? 'it' : 'them'} now, late.${tail}`;
  const which = passed === 1 ? 'the one' : `the ${passed}`;
  return `Approving posts ${which} whose time passed now, late, and the rest as planned.${tail}`;
}

/** What Post now and Skip do for posts that didn't go out: one, or a channel's several. */
export function missedEffect(n: number) {
  return n === 1
    ? 'Posting now sends it right away. Skipping drops it; nothing else changes.'
    : `Posting now sends all ${n} right away. Skipping drops them; nothing else changes.`;
}

/**
 * A post too late to go out (Prototype v16, UX review round 9): more than 48
 * hours late, or its text names a day that has passed. Skip leads, a fresh
 * post is beside it, and the reason ("3 days late, old news by now") is said.
 */
export function staleEffect(reason: string, n: number) {
  const why = `${reason[0].toUpperCase()}${reason.slice(1)}`;
  const it = n === 1 ? ['it’s', 'it'] : ['they’re', 'them'];
  return `${why}, so ${it[0]} better not posted. Skipping drops ${it[1]}; nothing else changes. Write a fresh post to share the link again.`;
}

/** The toast after Ask for changes: the note, cut to 57 characters, or what comes back. */
export const sentToAgent = (note: string, what = 'version') => {
  const n = note.trim();
  return n
    ? `Sent to your agent: “${n.length > 60 ? `${n.slice(0, 57)}…` : n}”`
    : `Sent to your agent. It comes back here as a new ${what}.`;
};

/**
 * An article body stored as plain text (feed articles read before ingest kept
 * the source's markup) has no tags, only one block per line. The preview shows
 * it as the site does (smagent-sites plainBodyHtml): one paragraph per line.
 * A body with markup is returned as it is.
 */
export function plainBodyHtml(body: string) {
  if (!body || /<[a-z!/]/i.test(body)) return body;
  return body
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map(
      (line) => `<p>${line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`
    )
    .join('\n');
}

/** Who proposed a waiting version, as the server says (tinycms/services/proposedBy.js). */
export interface ProposedBy {
  kind: 'person' | 'feed' | 'agent';
  name: string | null;
}

// The product's name, or a default label, isn't an agent's name: read as the
// actor it says "TinyCMS wants to publish this" (feedback 9f70af88).
const UNNAMED = /^(tiny ?cms\.?|ai agent|agent|your agent)$/i;

/** An agent's own name, or null for a default label or the product's. */
export const agentOwnName = (name?: string | null) => {
  const n = String(name || '').trim();
  return n && !UNNAMED.test(n) ? n : null;
};

/** "your NASA Earth Observatory feed", "your feed earthobservatory.nasa.gov", "your Tech feed". */
const feedWords = (name: string) =>
  /[./]/.test(name)
    ? `your feed ${name}`
    : /\bfeed$/i.test(name)
      ? `your ${name}`
      : `your ${name} feed`;

/**
 * The queue's "who" line (Prototype: "proposed by Monte Carlo Cowork"): the
 * agent's name, the feed it came in from, or the person who saved it.
 * `fallback` when the server didn't say.
 */
export function proposerLine(p: ProposedBy | null | undefined, fallback: string) {
  if (!p) return fallback;
  if (p.kind === 'feed' && p.name) return `from ${feedWords(p.name)}`;
  if (p.kind === 'person') return p.name ? `saved by ${p.name}` : fallback;
  if (p.kind === 'agent') return `proposed by ${agentOwnName(p.name) || 'your agent'}`;
  return fallback;
}

/**
 * The article page's banner over a version waiting for a yes: who, what, and
 * the mark beside it (the agent's initials, ✦ for an unnamed one, ⤓ for a feed).
 */
export function proposerBanner(p: ProposedBy | null | undefined, connectorLabel?: string | null) {
  if (p?.kind === 'feed' && p.name) {
    const feed = feedWords(p.name);
    return { who: `From ${feed}`, what: 'waiting for your approval', mark: '⤓' };
  }
  const name = agentOwnName(p?.kind === 'agent' ? p.name : null) || agentOwnName(connectorLabel);
  return {
    who: name || 'Your agent',
    what: 'wants to publish this',
    mark: name
      ? name
          .split(/\s+/)
          .map((w) => w[0])
          .join('')
          .slice(0, 2)
          .toUpperCase()
      : '✦',
  };
}
